import { useEffect, useMemo } from 'react';

import type { Person } from '@/store/persons/person.dto';
import { usePerson } from '@/store/persons/person.store';

export type PersonsMap = Map<string, Person>;

/**
 * The account's people by id, fetched once per session.
 *
 * `refresh` re-reads the list on mount, for screens that name a person picked
 * elsewhere — crew added after the first fetch are otherwise missing from it.
 */
export const usePersonsMap = ({ refresh = false }: { refresh?: boolean } = {}): PersonsMap => {
  const { people, hasFetchedPeople, loading, error, getPeople } = usePerson();

  useEffect(() => {
    if (refresh) void getPeople();
  }, [refresh, getPeople]);

  useEffect(() => {
    if (!hasFetchedPeople && !loading && !error) {
      void getPeople();
    }
  }, [hasFetchedPeople, loading, error, getPeople]);

  return useMemo(() => {
    const map = new Map<string, Person>();
    if (Array.isArray(people)) {
      for (const person of people) {
        map.set(person.id, person);
      }
    }
    return map;
  }, [people]);
};
